import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  CircularProgress,
  Alert,
  Box,
  Typography
} from '@mui/material';
import { Psychology as PsychologyIcon, AutoAwesome as AutoAwesomeIcon } from '@mui/icons-material';
import { GoogleGenerativeAI } from '@google/generative-ai';

export default function AISuggestions({ open, onClose, onSelect, selectedMember, previousPoints }) {
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      handleGenerateSuggestions();
    } else {
      setSuggestions([]);
      setSelected([]);
      setError(null);
    }
  }, [open]);

  const handleToggle = (suggestion) => {
    const currentIndex = selected.findIndex(s => s === suggestion);
    const newSelected = [...selected];

    if (currentIndex === -1) {
      newSelected.push(suggestion);
    } else {
      newSelected.splice(currentIndex, 1);
    }

    setSelected(newSelected);
  };

  const handleGenerateSuggestions = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GOOGLE_AI_API_KEY);
      const model = genAI.getGenerativeModel({ model: 'gemini-2.0-flash' });

      const prompt = `
        As an AI assistant for 1:1 meetings between a manager and their team member, 
        generate 5-7 relevant talking points for a meeting with a ${selectedMember?.role || 'team member'}.
        
        Context:
        - Employee Name: ${selectedMember?.name || 'Team Member'}
        - Employee Role: ${selectedMember?.role || 'team member'}
        - Previous Meeting Points: ${previousPoints?.join(', ') || 'None'}
        
        Generate talking points that:
        1. Are specific and actionable
        2. Cover both work progress and professional development
        3. Address potential challenges or blockers
        4. Include follow-ups from previous points if relevant
        5. Encourage open discussion
        
        Format each point as a clear, concise statement.

        Format the talking point without any text formatting, like this:
        Discuss the status of current projects
        Seek input from Jane on any obstacles or barriers that are impacting her productivity or project progress, and brainstorm solutions together
        Acknowledge Jane's contributions and achievements, and provide constructive feedback to help her further develop her skills and knowledge
      `;

      const result = await model.generateContent(prompt);
      const response = await result.response;
      const text = response.text();

      // Parse the response into individual talking points
      const suggestions = text
        .split('\n')
        .filter(line => line.trim())
        .map(line => line.replace(/^[-*\d.]+\s*/, '').trim());

      setSuggestions(suggestions);
    } catch (error) {
      console.error('AI Suggestion Error:', error);
      setError('Failed to generate suggestions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    onSelect(selected);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 2
      }}>
        <Typography variant="h6">AI-Suggested Talking Points</Typography>
        <Button 
          onClick={handleGenerateSuggestions}
          startIcon={<AutoAwesomeIcon />}
          disabled={loading}
          sx={{
            backgroundColor: 'grey.100',
            color: 'text.primary',
            textTransform: 'none',
            border: '1px solid transparent',
            '&:hover': {
              backgroundColor: 'common.white',
              border: '1px solid',
              borderColor: 'primary.main',
            }
          }}
        >
          Regenerate
        </Button>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <List>
            {suggestions.map((suggestion, index) => (
              <ListItem
                key={index}
                dense
                button
                onClick={() => handleToggle(suggestion)}
              >
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={selected.includes(suggestion)}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText 
                  primary={suggestion}
                  primaryTypographyProps={{
                    style: { whiteSpace: 'pre-wrap', wordWrap: 'break-word' }
                  }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleConfirm}
          variant="contained"
          disabled={selected.length === 0}
        >
          Add Selected Points
        </Button>
      </DialogActions>
    </Dialog>
  );
}
